// src/pages/DetailedScoringReportPage.js
// Updated: 11/09/2025 08:42 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Printer, User, MessageSquare, Award } from 'lucide-react';
import { calculateResults } from '../utils/scoringCalculator';

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

/**
 * Main component to render the full, detailed report for a session
 */
export default function DetailedScoringReportPage() {
  const { sessionId } = useParams();
  const { userData, isAdmin } = useAuth();

  const [session, setSession] = useState(null);
  const [packages, setPackages] = useState([]);
  const [scores, setScores] = useState([]);
  const [reviewers, setReviewers] = useState(new Map());
  const [summaryResults, setSummaryResults] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch all data for the report
  useEffect(() => {
    if (!sessionId || !userData?.orgId) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        // 1. Check if user is an admin (client-side check)
        if (!isAdmin) {
          throw new Error("Access Denied: You must be an admin to view this detailed report.");
        }

        // 2. Fetch Session
        const sessionRef = doc(db, 'scoringSessions', sessionId);
        const sessionSnap = await getDoc(sessionRef);
        if (!sessionSnap.exists() || sessionSnap.data().orgId !== userData.orgId) {
          throw new Error("Session not found or access denied.");
        }
        const sessionData = { id: sessionSnap.id, ...sessionSnap.data() };
        setSession(sessionData);

        // 3. Fetch Packages
        const packagesQuery = query(
          collection(db, 'scoringSessions', sessionId, 'packages'),
          orderBy('name', 'asc')
        );
        const packagesSnap = await getDocs(packagesQuery);
        const packagesData = packagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPackages(packagesData);

        // 4. Fetch All Scores
        const scoresQuery = query(collection(db, 'scoringSessions', sessionId, 'scores'));
        const scoresSnap = await getDocs(scoresQuery);
        const scoresData = scoresSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setScores(scoresData);

        // 5. Fetch Reviewer Details
        const reviewerIds = sessionData.reviewers || [];
        if (reviewerIds.length > 0) {
          // --- FIX: Query only users in the correct org ---
          const userQuery = query(
            collection(db, 'users'),
            where('__name__', 'in', reviewerIds),
            where('orgId', '==', userData.orgId)
          );
          const usersSnap = await getDocs(userQuery);
          const userMap = new Map();
          usersSnap.forEach(doc => userMap.set(doc.id, doc.data()));
          setReviewers(userMap);
        }

        // 6. CALCULATE SUMMARY RESULTS
        const summary = calculateResults(sessionData, packagesData, scoresData);
        setSummaryResults(summary);

      } catch (err) {
        console.error("Error fetching report data:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [sessionId, userData?.orgId, isAdmin]);

  // Create a sorted list of reviewers for consistent column order
  const sortedReviewers = useMemo(() => {
    if (!session?.reviewers || reviewers.size === 0) return [];
    return session.reviewers
      .map(id => ({ id, ...reviewers.get(id) }))
      .filter(r => r.displayName); // Filter out any that failed to load
  }, [session, reviewers]);

  // --- 1. NEW: Create a map for reviewer names (anonymized or real) ---
  const reviewerIdToDisplayNameMap = useMemo(() => {
    const newMap = new Map();
    if (session?.isAnonymous) {
      // Anonymous: Map [userId] -> "Reviewer X"
      sortedReviewers.forEach((reviewer, index) => {
        newMap.set(reviewer.id, `Reviewer ${index + 1}`);
      });
    } else {
      // Public: Map [userId] -> "Real Name"
      sortedReviewers.forEach((reviewer) => {
        newMap.set(reviewer.id, reviewer.displayName);
      });
    }
    return newMap;
  }, [sortedReviewers, session?.isAnonymous]);
  // --- END NEW MEMO ---

  // Process data for detailed breakdown
  const reportData = useMemo(() => {
    return packages.map(pkg => {
      const packageScores = scores.filter(s => s.packageId === pkg.id);
      return {
        ...pkg,
        scores: packageScores,
      };
    });
  }, [packages, scores]);

  const handlePrint = () => {
    document.body.classList.add('printing-scoring-report');
    window.print();
    document.body.classList.remove('printing-scoring-report');
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <p className="text-red-600 p-4 bg-red-50 border border-red-200 rounded">{error}</p>;
  if (!session) return <p>Session not found.</p>;

  const maxPossibleRaw = summaryResults[0]?.totalPossibleRaw ?? 0;
  const maxPossibleWeighted = summaryResults[0]?.totalPossibleWeighted ?? 0;

  return (
    <div>
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-6 print-hide">
        <Link
          to="/scoring"
          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Scoring Sessions
        </Link>
        <button
          onClick={handlePrint}
          className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          <Printer className="mr-2 h-5 w-5" />
          Print Official Report
        </button>
      </div>

      {/* Main Report Content */}
      <div className="rounded-lg bg-white p-8 shadow-lg print-this-report print:shadow-none print:p-0">
        {/* Report Header */}
        <header className="border-b-2 border-gray-900 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Official Scoring Report</h1>
          <h2 className="text-2xl font-semibold text-blue-700 mt-2">{session.name}</h2>
          <p className="mt-1 text-sm text-gray-500">
            Session Status: {session.status} | Generated: {new Date().toLocaleString()}
            {/* --- 2. NEW: Add Anonymity status to header --- */}
            {session.isAnonymous && (
              <span className="font-semibold text-red-600 ml-2"> | ANONYMOUS REPORT</span>
            )}
            {/* --- END NEW --- */}
          </p>
          <p className="mt-2 text-gray-700">{session.description}</p>
        </header>

        {/* --- Summary Section (No changes) --- */}
        <section className="mt-8 page-break-inside-avoid print:break-after-page">
          <h3 className="text-2xl font-semibold text-gray-800 border-b-2 border-blue-600 pb-2">
            Final Summary & Rankings
          </h3>
          {summaryResults.length === 0 ? (
            <p className="text-gray-500 p-4 bg-gray-50 border rounded mt-4">No scores submitted yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg bg-white shadow-md border mt-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Rank</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Package</th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Reviewers</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Avg. Raw Score (/ {maxPossibleRaw.toFixed(0)})</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Avg. Weighted Score (/ {maxPossibleWeighted.toFixed(1)})</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {summaryResults.map((result, index) => (
                    <tr key={result.id} className={result.rank === 1 ? 'bg-yellow-50' : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          {result.rank === 1 && <Award className="mr-2 h-5 w-5 text-yellow-500" />}
                          #{result.rank}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-800">{result.name}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-center text-gray-500">{result.numReviewers}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-right text-gray-500">{result.avgRawScore.toFixed(2)}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-right font-bold text-gray-700">{result.avgWeightedScore.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        {/* --- END SUMMARY SECTION --- */}

        {/* --- Detailed Breakdown (Starts on Page 2) --- */}
        <div className="mt-16 space-y-12 print:mt-12">
          <h3 className="text-2xl font-semibold text-gray-800 border-b-2 border-blue-600 pb-2 print:break-after-avoid">
            Detailed Breakdown by Package
          </h3>

          {reportData.length === 0 && !isLoading && (
            <p className="text-gray-500">No packages or scores were found for this session.</p>
          )}

          {/* === 1. Loop through each Package === */}
          {reportData.map((pkg) => {
            const packageScores = scores.filter(s => s.packageId === pkg.id);

            return (
              <section key={pkg.id} className="page-break-inside-avoid">
                <h3 className="text-2xl font-semibold text-gray-800 border-b border-gray-300 pb-2">
                  Package: {pkg.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>

                {/* === 2. Loop through each Category === */}
                {session.categories.map((cat, catIndex) => (
                  <div key={catIndex} className="mt-6 page-break-inside-avoid">
                    <h4 className="text-xl font-semibold text-gray-700">{cat.name}</h4>

                    {/* --- Rubric Grid Table --- */}
                    <div className="overflow-x-auto mt-2 border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Criterion
                            </th>
                            {/* --- 3. APPLY ANONYMITY TO HEADER --- */}
                            {sortedReviewers.map(r => (
                              <th key={r.id} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                                {reviewerIdToDisplayNameMap.get(r.id) || '...'}
                              </th>
                            ))}
                            {/* --- END HEADER CHANGE --- */}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {session.useSimplifiedRubric ? (
                            <tr>
                              <td className="px-4 py-3 text-sm font-medium text-gray-800">
                                {cat.simplifiedCriteria || 'Score'}
                              </td>
                              {sortedReviewers.map(r => {
                                const scoreDoc = packageScores.find(s => s.reviewerId === r.id);
                                const scoreVal = scoreDoc?.scores?.[`${catIndex}`];
                                return (
                                  <td key={r.id} className="px-4 py-3 text-center text-sm">
                                    {scoreVal ?? 'N/A'} / {cat.maxPoints}
                                  </td>
                                );
                              })}
                            </tr>
                          ) : (
                            cat.criteria.map((crit, critIndex) => (
                              <tr key={critIndex}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-800">
                                  {crit.name}
                                  <span className="ml-1 text-xs text-gray-500">(W: {crit.weight})</span>
                                </td>
                                {sortedReviewers.map(r => {
                                  const scoreDoc = packageScores.find(s => s.reviewerId === r.id);
                                  const scoreVal = scoreDoc?.scores?.[`${catIndex}_${critIndex}`];
                                  return (
                                    <td key={r.id} className="px-4 py-3 text-center text-sm">
                                      {scoreVal ?? 'N/A'} / {crit.maxPoints}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* --- Category Remarks Section --- */}
                    <div className="mt-4 page-break-inside-avoid">
                      <h5 className="text-md font-semibold text-gray-600 flex items-center">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Category Remarks
                      </h5>
                      <div className="mt-2 space-y-2">
                        {/* --- 4. APPLY ANONYMITY TO REMARKS --- */}
                        {packageScores.map(score => {
                          const remark = score.categoryRemarks?.[`cat_remark_${catIndex}`];
                          return remark ? (
                            <div key={score.id} className="text-sm rounded-md bg-gray-50 p-2 border-l-4 border-gray-300">
                              <span className="font-semibold">
                                {reviewerIdToDisplayNameMap.get(score.reviewerId) || 'Unknown'}:
                              </span>
                              <span className="italic ml-1">"{remark}"</span>
                            </div>
                          ) : null;
                        })}
                        {/* --- END REMARKS CHANGE --- */}
                      </div>
                    </div>
                  </div>
                ))}

                {/* === 3. Package Totals & General Comments === */}
                <div className="mt-8 page-break-inside-avoid">
                  <h4 className="text-xl font-semibold text-gray-700">Package Totals & Comments</h4>

                  {/* --- Totals Table --- */}
                  <div className="overflow-x-auto mt-2 border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Reviewer
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Total Raw Score
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                            Total Weighted Score
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* --- 5. APPLY ANONYMITY TO TOTALS TABLE --- */}
                        {sortedReviewers.map(r => {
                          const scoreDoc = packageScores.find(s => s.reviewerId === r.id);
                          return (
                            <tr key={r.id}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-800">
                                {reviewerIdToDisplayNameMap.get(r.id) || '...'}
                              </td>
                              <td className="px-4 py-3 text-center text-sm">{scoreDoc?.totalRawScore.toFixed(0) ?? 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-sm font-bold">{scoreDoc?.totalWeightedScore.toFixed(2) ?? 'N/A'}</td>
                            </tr>
                          );
                        })}
                        {/* --- END TOTALS CHANGE --- */}
                      </tbody>
                    </table>
                  </div>

                  {/* --- General Comments Section --- */}
                  <div className="mt-4">
                    <h5 className="text-md font-semibold text-gray-600 flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      General Comments
                    </h5>
                    <div className="mt-2 space-y-2">
                      {/* --- 6. APPLY ANONYMITY TO GENERAL COMMENTS --- */}
                      {packageScores.map(score => (
                        score.generalComments ? (
                          <div key={score.id} className="text-sm rounded-md bg-gray-50 p-2 border-l-4 border-gray-300">
                            <span className="font-semibold">
                              {reviewerIdToDisplayNameMap.get(score.reviewerId) || 'Unknown'}:
                            </span>
                            <span className="italic ml-1">"{score.generalComments}"</span>
                          </div>
                        ) : null
                      ))}
                      {/* --- END GENERAL COMMENTS CHANGE --- */}
                    </div>
                  </div>
                </div>

              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}